import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Search, Plus, RefreshCcw, CheckCircle, AlertTriangle, AlertCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Layers, ShoppingCart, DollarSign, Tag, Star, TrendingUp, Box, Truck, Phone, Mail, MapPin, FileText, List, LayoutList, Building, Network, Clock } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencyOptions, getBrandLocale, getBrandPriceTierThresholds, normalizeBrandCurrency } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableScrollClass,
} from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { wrapControlsStripSearch } from '../../shared/utils/listPageSearchVisibility';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { buildAchatsColumnDefs, buildAchatsImportExportProps, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, resolveAchatsEntityReference, AchatsConfigNotice, isAchatsCatalogLowStock, useAchatsListViewMode, useAchatsListDisplay, achatsSettingsFromConfig, fetchAchatsSuppliers } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import { useArticlesConfig } from '../articles/useArticlesConfig';
import { fetchCatalogProducts } from '../articles/catalogProductOptions';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const CatalogueFournisseurPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.catalogueFournisseurPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const achatsSettings = achatsSettingsFromConfig(achatsConfig);
  useArticlesConfig();
  const dateLocale = getBrandLocale(i18n.language);
  const priceTiers = getBrandPriceTierThresholds();

  const getStatusLabel = (status) => {
    const map = {
      disponible: p('status_available'),
      rupture: p('status_out_of_stock'),
      en_commande: p('status_on_order'),
      discontinue: p('status_discontinued'),
    };
    return map[status] || status || p('em_dash');
  };

  const UNIT_OPTIONS = [
    { value: 'pièce', labelKey: 'unit_piece' },
    { value: 'kg', labelKey: 'unit_kg' },
    { value: 'litre', labelKey: 'unit_litre' },
    { value: 'mètre', labelKey: 'unit_metre' },
    { value: 'boîte', labelKey: 'unit_box' },
    { value: 'carton', labelKey: 'unit_carton' },
  ];

  const getUnitLabel = (unit) => {
    const match = UNIT_OPTIONS.find((option) => option.value === unit);
    return match ? p(match.labelKey) : unit || p('unit_piece');
  };

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnCategoryButton = event.target.closest('[data-category-dropdown-button]');
      const isClickInCategoryDropdown = event.target.closest('[data-category-dropdown]');
      if (!isClickOnCategoryButton && !isClickInCategoryDropdown) {
        setShowCategoryDropdown(false);
      }
      
      const isClickOnUnitButton = event.target.closest('[data-unit-dropdown-button]');
      const isClickInUnitDropdown = event.target.closest('[data-unit-dropdown]');
      if (!isClickOnUnitButton && !isClickInUnitDropdown) {
        setShowUnitDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [catalogueItems, setCatalogueItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [isHierarchyView, setIsHierarchyView] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Collapsible sections state
  const [formTab, setFormTab] = useState('mainInfo');
  const formTabs = useMemo(() => ([
    { id: 'mainInfo', label: p('section_product_supplier'), icon: Package, iconColor: 'text-blue-400' },
    { id: 'pricingStock', label: p('section_pricing_stock'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    catalog_code: '',
    product_id: '',
    product_code: '',
    product_name: '',
    description: '',
    category_id: '',
    category: '',
    supplier_id: '',
    supplier_name: '',
    supplier_code: '',
    unit_price: '',
    currency: getBrandDefaultCurrency(),
    unit: 'pièce',
    min_order_qty: '',
    stock_quantity: '',
    reorder_level: '',
    delivery_time: '',
    notes: '',
    status: 'disponible'
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_items');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  // Dropdown data states
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Track which suppliers are expanded
  const [expandedSuppliers, setExpandedSuppliers] = useState({});
  
  const dateRangeRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const unitDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { 
    loadCatalogue(); 
    loadSuppliers();
    loadProducts();
    loadCategories();
  }, []);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCatalogue = async () => {
    try {
      const response = await localApiAdapter.get('/achats/catalogue-fournisseurs');
      const raw = response?.data || response || [];
      const items = (Array.isArray(raw) ? raw : []).map((item) => ({
        ...item,
        product_code: item.product_code || item.supplier_sku || item.catalog_code || '',
        category: item.category_name || item.category || '',
        unit: item.unit_of_measure || item.unit || '',
        status: item.status || (item.is_active ? 'disponible' : 'rupture'),
        delivery_time: item.lead_time_days ?? item.delivery_time ?? '',
      }));
      setCatalogueItems(sortRecordsNewestFirst(items));
    } catch (error) {
      console.error('Error loading catalogue:', error);
      setCatalogueItems([]);
    }
  };

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      setAvailableSuppliers(await fetchAchatsSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const loadProducts = async () => {
    try {
      setLoadingProducts(true);
      setAvailableProducts(await fetchCatalogProducts());
    } catch (error) {
      console.error('Error loading products:', error);
      setAvailableProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await localApiAdapter.get('/categories');
      setAvailableCategories(response?.data || response || []);
    } catch (error) {
      console.error('Error loading categories:', error);
      setAvailableCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Toggle supplier group expansion
  const toggleSupplierGroup = (supplierId) => {
    setExpandedSuppliers(prev => ({
      ...prev,
      [supplierId]: !prev[supplierId]
    }));
  };

  const handleSupplierSelection = (supplierId) => {
    if (!supplierId) {
      setFormData(prev => ({ ...prev, supplier_id: '', supplier_name: '', supplier_code: '' }));
      return;
    }
    const supplier = availableSuppliers.find(s => s.id.toString() === supplierId.toString());
    if (supplier) {
      setFormData(prev => ({ 
        ...prev, 
        supplier_id: supplierId,
        supplier_name: supplier.company_name || supplier.name || '',
        supplier_code: supplier.supplier_code || ''
      }));
    }
  };

  const handleProductSelection = (productId) => {
    if (!productId) {
      setFormData(prev => ({ ...prev, product_id: '', product_name: '', product_code: '' }));
      return;
    }
    const product = availableProducts.find(p => p.id.toString() === productId.toString());
    if (product) {
      setFormData(prev => ({ 
        ...prev, 
        product_id: productId,
        product_name: product.name || '',
        product_code: product.code || '',
        unit_price: product.price || ''
      }));
    }
  };

  const handleCategorySelection = (categoryId) => {
    if (!categoryId) {
      setFormData(prev => ({ ...prev, category_id: '', category: '' }));
      return;
    }
    const category = availableCategories.find(c => c.id.toString() === categoryId.toString());
    if (category) {
      setFormData(prev => ({ 
        ...prev, 
        category_id: categoryId,
        category: category.name || ''
      }));
    }
  };

  const stats = {
    total: catalogueItems.length,
    disponible: catalogueItems.filter(item => item.status === 'disponible').length,
    rupture: catalogueItems.filter(item => item.status === 'rupture').length,
    commande: catalogueItems.filter(item => item.status === 'en_commande').length,
    totalValue: catalogueItems.reduce((sum, item) => sum + (parseFloat(item.unit_price) || 0) * (parseInt(item.stock_quantity) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_items', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_categorie', name: p('tab_by_category'), icon: Layers },
    { id: 'par_prix', name: p('tab_by_price'), icon: DollarSign },
    { id: 'par_delai', name: p('tab_by_delivery'), icon: Truck },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Package },
    { id: 'disponible', name: p('filter_available'), icon: CheckCircle },
    { id: 'rupture', name: p('filter_out_of_stock'), icon: AlertCircle },
    { id: 'commande', name: p('filter_on_order'), icon: ShoppingCart },
  ];

  const catalogueDistribution = {
    all_items: {
      title: p('overview_title'),
      data: [
        { filterKey: 'disponible', name: p('dist_available'), count: stats.disponible, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'rupture', name: p('dist_out_of_stock'), count: stats.rupture, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'commande', name: p('dist_on_order'), count: stats.commande, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
      ],
    },
    par_categorie: {
      title: p('dist_by_category_title'),
      data: [
        { filterKey: 'matiere_premiere', name: p('cat_raw_materials'), count: catalogueItems.filter(i => i.category === 'matiere_premiere').length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'fourniture', name: p('cat_supplies'), count: catalogueItems.filter(i => i.category === 'fourniture').length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { filterKey: 'equipement', name: p('cat_equipment'), count: catalogueItems.filter(i => i.category === 'equipement').length, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
      ],
    },
    par_prix: {
      title: p('dist_by_price_title'),
      data: [
        { filterKey: 'price_low', name: p('price_low'), count: catalogueItems.filter(i => parseFloat(i.unit_price) < priceTiers.lowMax).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'price_medium', name: p('price_medium'), count: catalogueItems.filter(i => parseFloat(i.unit_price) >= priceTiers.lowMax && parseFloat(i.unit_price) <= priceTiers.highMin).length, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'price_high', name: p('price_high'), count: catalogueItems.filter(i => parseFloat(i.unit_price) > priceTiers.highMin).length, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_delai: {
      title: p('dist_by_delivery_title'),
      data: [
        { filterKey: 'express', name: p('delivery_express'), count: catalogueItems.filter(i => parseInt(i.delivery_time) <= 2).length, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'fast', name: p('delivery_fast'), count: catalogueItems.filter(i => parseInt(i.delivery_time) >= 3 && parseInt(i.delivery_time) <= 7).length, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { filterKey: 'standard', name: p('delivery_standard'), count: catalogueItems.filter(i => parseInt(i.delivery_time) > 7).length, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    },
  };

  const distributionInfo = catalogueDistribution[mainViewMode] || catalogueDistribution.all_items;

  const baseFilteredCatalogue = catalogueItems.filter(item => {
    if (dateRange.start || dateRange.end) {
      const itemDate = new Date(item.created_at);
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (itemDate < startOfDay) return false;
      }
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (itemDate > endOfDay) return false;
      }
    }
    
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    
    if (searchTerm && !item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !item.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_items') {
      if (quickFilter === 'disponible' && item.status !== 'disponible') return false;
      if (quickFilter === 'rupture' && item.status !== 'rupture') return false;
      if (quickFilter === 'commande' && item.status !== 'en_commande') return false;
    }
    
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_items':
          if (activeDistributionFilter === 'disponible' && item.status !== 'disponible') return false;
          if (activeDistributionFilter === 'rupture' && item.status !== 'rupture') return false;
          if (activeDistributionFilter === 'commande' && item.status !== 'en_commande') return false;
          break;
        case 'par_categorie':
          if (activeDistributionFilter === 'matiere_premiere' && item.category !== 'matiere_premiere') return false;
          if (activeDistributionFilter === 'fourniture' && item.category !== 'fourniture') return false;
          if (activeDistributionFilter === 'equipement' && item.category !== 'equipement') return false;
          break;
        case 'par_prix': {
          const price = parseFloat(item.unit_price) || 0;
          if (activeDistributionFilter === 'price_low' && price >= priceTiers.lowMax) return false;
          if (activeDistributionFilter === 'price_medium' && (price < priceTiers.lowMax || price > priceTiers.highMin)) return false;
          if (activeDistributionFilter === 'price_high' && price <= priceTiers.highMin) return false;
          break;
        }
        case 'par_delai': {
          const delai = parseInt(item.delivery_time) || 0;
          if (activeDistributionFilter === 'express' && delai > 2) return false;
          if (activeDistributionFilter === 'fast' && (delai < 3 || delai > 7)) return false;
          if (activeDistributionFilter === 'standard' && delai <= 7) return false;
          break;
        }
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('catalog_code', (r) => r.catalog_code || ''),
      textColumnRule('product_name'),
      textColumnRule('supplier_name'),
      textColumnRule('unit_price', (r) => String(r.unit_price ?? '')),
      textColumnRule('stock_quantity', (r) => String(r.stock_quantity ?? '')),
      exactColumnRule('status', (r) => r.status || ''),
    ],
    [],
  );

  const { filtered: filteredCatalogue, displayed: displayedCatalogue } = useAchatsListDisplay(
    baseFilteredCatalogue,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedRecords, filteredCatalogue);

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  // Group catalog items by supplier (MUST be after filteredCatalogue is defined)
  const groupedBySupplier = filteredCatalogue.reduce((groups, item) => {
    const supplierId = item.supplier_id || 'unknown';
    // Fallback: lookup supplier name from availableSuppliers if missing
    let supplierName = item.supplier_name;
    if (!supplierName && supplierId !== 'unknown') {
      const supplier = availableSuppliers.find(s => s.id.toString() === supplierId.toString());
      supplierName = supplier ? (supplier.company_name || supplier.name) : p('unknown_supplier');
    } else if (!supplierName) {
      supplierName = p('unknown_supplier');
    }
    
    if (!groups[supplierId]) {
      groups[supplierId] = {
        id: supplierId,
        name: supplierName,
        products: []
      };
    }
    
    groups[supplierId].products.push(item);
    return groups;
  }, {});

  const supplierGroups = Object.values(groupedBySupplier);

  const generateCatalogCode = useCallback(
    () => resolveAchatsEntityReference(achatsSettings, 'supplierCatalog', 'SCAT', catalogueItems, (item) => item.catalog_code),
    [achatsSettings, catalogueItems],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const catalogCode = resolveAchatsEntityReference(
        achatsSettings,
        'supplierCatalog',
        'SCAT',
        catalogueItems,
        (item) => item.catalog_code,
        formData.catalog_code,
      );
      // Map frontend fields to backend expected fields
      console.log('📝 Form data before submit:', formData);
      const submitData = { 
        catalog_code: catalogCode,
        product_id: formData.product_id,
        product_name: formData.product_name,
        product_code: formData.product_code,
        category_id: formData.category_id,
        category_name: formData.category,
        supplier_id: formData.supplier_id,
        supplier_name: formData.supplier_name,
        supplier_code: formData.supplier_code,
        supplier_sku: formData.product_code || null,
        supplier_product_name: formData.product_name || null,
        description: formData.description,
        unit_price: parseFloat(formData.unit_price) || 0,
        currency: formData.currency || getBrandDefaultCurrency(),
        unit_of_measure: formData.unit || 'pièce',
        moq: parseInt(formData.min_order_qty) || 1,
        lead_time_days: parseInt(formData.delivery_time) || 0,
        stock_quantity: parseInt(formData.stock_quantity) || 0,
        reorder_level: parseInt(formData.reorder_level) || 0,
        volume_discounts: null,
        valid_from: null,
        valid_until: null,
        is_preferred: 0,
        is_active: formData.status === 'disponible' ? 1 : 0,
        last_purchase_price: null,
        last_purchase_date: null,
        total_purchases: 0,
        average_delivery_days: 0,
        supplier_rating: 0,
        quality_rating: 0,
        price_history: null,
        notes: formData.notes || '',
        attachment_path: null,
        status: formData.status || 'disponible'
      };
      
      console.log('📤 Submitting data:', submitData);
      console.log('✏️ Editing mode:', !!editingRecord, editingRecord?.id);
      
      if (editingRecord) {
        await localApiAdapter.put(`/achats/catalogue-fournisseurs/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/catalogue-fournisseurs', submitData);
        notifyCreated(showNotify, entityLabel);
      }
      
      console.log('✅ Save successful!');
    } catch (error) {
      console.error('Error saving:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      const errorMessage = error?.response?.data?.error || error?.message || error?.toString() || p('save_error_unknown');
      notifyError(showNotify, p('alert_save_error_prefix') + ' ' + errorMessage);
      return; // Don't reset form on error
    }
    // Only reset and reload if successful
    resetForm();
    loadCatalogue();
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      catalog_code: record.catalog_code || '',
      product_id: record.product_id?.toString() || '',
      product_code: record.product_code || record.supplier_sku || record.catalog_code || '',
      product_name: record.product_name || '',
      description: record.description || '',
      category_id: record.category_id?.toString() || '',
      category: record.category_name || record.category || '',
      supplier_id: record.supplier_id?.toString() || '',
      supplier_name: record.supplier_name || '',
      supplier_code: record.supplier_code || '',
      unit_price: record.unit_price?.toString() || '',
      currency: record.currency || getBrandDefaultCurrency(),
      unit: record.unit_of_measure || record.unit || 'pièce',
      min_order_qty: record.moq?.toString() || record.min_order_qty?.toString() || '',
      stock_quantity: record.stock_quantity?.toString() || '',
      reorder_level: record.reorder_level?.toString() || '',
      delivery_time: record.lead_time_days?.toString() || record.delivery_time?.toString() || '',
      notes: record.notes || '',
      status: record.status || 'disponible'
    });
    setFormTab('mainInfo');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/catalogue-fournisseurs/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadCatalogue();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error_prefix', { defaultValue: p('save_error_unknown') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/achats/catalogue-fournisseurs/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedRecords([]);
        loadCatalogue();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error_prefix', { defaultValue: p('save_error_unknown') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      catalog_code: '',
      product_id: '',
      product_code: '',
      product_name: '',
      description: '',
      category_id: '',
      category: '',
      supplier_id: '',
      supplier_name: '',
      supplier_code: '',
      unit_price: '',
      currency: getBrandDefaultCurrency(),
      unit: 'pièce',
      min_order_qty: '',
      stock_quantity: '',
      reorder_level: '',
      delivery_time: '',
      notes: '',
      status: 'disponible'
    });
    setEditingRecord(null);
    setFormTab('mainInfo');
    setShowModal(false);
  };

  const handleExport = () => {
    const headers = [
      { key: 'catalog_code', label: p('label_code') },
      { key: 'product_code', label: p('col_product_code') },
      { key: 'product_name', label: p('col_product_name') },
      { key: 'category_name', label: p('col_category') },
      { key: 'supplier_name', label: p('label_supplier') },
      { key: 'unit_price', label: p('view_unit_price') },
      { key: 'unit_of_measure', label: p('col_unit') },
      { key: 'lead_time_days', label: p('col_delivery') },
      { key: 'is_active', label: p('col_status') }
    ];

    const fileName = `catalogue_fournisseur_export_${new Date().toISOString().split('T')[0]}`;
    
    try {
      exportToExcel(filteredCatalogue, headers, `${fileName}.xlsx`);
    } catch (error) {
      console.error('Error during export:', error);
      alert(p('alert_export_error'));
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('label_code')}</th>
                <th>${p('col_product')}</th>
                <th>${p('label_supplier')}</th>
                <th>${p('col_price')}</th>
                <th>${p('col_stock')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredCatalogue.map(item => `
                <tr>
                  <td>${item.product_code || ''}</td>
                  <td>${item.product_name || ''}</td>
                  <td>${item.supplier_name || ''}</td>
                  <td>${formatBrandMoney(item.unit_price || 0)}</td>
                  <td>${item.stock_quantity || '0'}</td>
                  <td>${item.status || ''}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('title'), {});
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleImport = () => {
    const headers = [
      { key: 'product_code', label: p('col_product_code') },
      { key: 'product_name', label: p('col_product_name') },
      { key: 'category', label: p('col_category') },
      { key: 'supplier_name', label: p('label_supplier') },
      { key: 'unit_price', label: p('view_unit_price') },
      { key: 'unit', label: p('col_unit') },
      { key: 'stock_quantity', label: p('col_stock') },
    ];

    const schema = {
      requiredFields: ['product_name', 'supplier_name', 'unit_price'],
      validators: {
        unit_price: (value) => !isNaN(parseFloat(value)) && parseFloat(value) >= 0
      }
    };

    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, headers, schema);
        
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post('/achats/catalogue-fournisseurs', record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          
          await loadCatalogue();
          
          const message = p('import_success', { total: result.totalCount, imported: successCount }) +
            (result.invalidCount > 0 ? '\n' + p('import_errors_suffix', { count: result.invalidCount }) + '\n\n' + result.errors.slice(0, 5).join('\n') : '');
          
          alert(message);
        }
      } catch (error) {
        console.error('Import error:', error);
        alert(p('alert_import_error'));
      }
    }, '.csv,.xlsx,.xls');
  };


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredCatalogue.map((r) => r.id));
    }
  };
  
  const handleSelectRecord = (id) => { 
    setSelectedRecords(selectedRecords.includes(id) ? 
      selectedRecords.filter(rId => rId !== id) : 
      [...selectedRecords, id]
    ); 
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/catalogue-fournisseurs/${recordId}`, { status: newStatus });
      await loadCatalogue();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const statusOptions = [
    { value: 'disponible', label: p('status_available'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'rupture', label: p('status_out_of_stock'), icon: AlertTriangle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
    { value: 'en_commande', label: p('status_on_order'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'discontinue', label: p('status_discontinued'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  ];

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>{Icon ? <Icon size={14} /> : null}{label}</span>;
  };

  const listStats = useMemo(
    () => [
      { key: 'total', label: p('stat_total_products'), value: stats.total, icon: Package, color: 'from-blue-500 to-blue-600', variant: 'copper' },
      { key: 'disponible', label: p('stat_available'), value: stats.disponible, icon: CheckCircle, color: 'from-green-500 to-green-600', variant: 'forest' },
      { key: 'rupture', label: p('stat_out_of_stock'), value: stats.rupture, icon: AlertCircle, color: 'from-red-500 to-red-600', variant: 'copper' },
      { key: 'commande', label: p('stat_on_order'), value: stats.commande, icon: ShoppingCart, color: 'from-amber-500 to-amber-600', variant: 'forest' },
    ],
    [stats, p],
  );

  const catalogueColumnKeys = useMemo(
    () => ['catalog_code', 'product_name', 'supplier_name', 'unit_price', 'stock_quantity', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(catalogueColumnKeys);

  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'catalog_code', labelKey: 'col_catalog_code', required: true },
      { key: 'product_name', labelKey: 'col_product' },
      { key: 'supplier_name', labelKey: 'label_supplier' },
      { key: 'unit_price', labelKey: 'col_price' },
      { key: 'stock_quantity', labelKey: 'col_stock' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, columnVisibility),
    [p, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);
  const hierarchyGroupColSpan = qawaemColumns.filter((c) => c.key !== 'actions' && c.key !== 'supplier_name' && c.checked !== false).length;

  const importExportProps = useMemo(
    () => buildAchatsImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      p,
      t,
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportExcel: handleExport,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, p, t],
  );

  const advancedOptionsContent = (
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button 
                  key={tab.id} 
                  onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} 
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          
          {mainViewMode === 'all_items' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button 
                    key={filter.id} 
                    onClick={() => setQuickFilter(filter.id)} 
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}
                  >
                    <filter.icon className="h-5 w-5" />
                    <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <XCircle size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.filterKey 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={p('filter_by_title', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.name ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.name ? 'text-white/80' : 'text-white/60'}`}>
                    {p('percent_of_catalogue', { percent: (() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })() })}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
  );

  return (
    <>
      <AchatsConfigNotice
        message={!achatsConfig.enableSupplierCatalog ? p('notice_catalog_disabled', { defaultValue: 'فهرس الموردين معطّل — فعّله من إعدادات المشتريات ← فهرس الموردين.' }) : null}
      />
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={Package}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildAchatsBulkDeleteFilterAction(selectedRecords.length, handleBulkDelete, { deleteLabel: `${t('common.delete')} (${selectedRecords.length})`, title: p('bulk_delete_confirm', { count: selectedRecords.length }) })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
        onCreate={() => {
          if (!achatsConfig.enableSupplierCatalog) {
            alert(p('feature_disabled', { defaultValue: 'فهرس الموردين معطّل من إعدادات المشتريات.' }));
            return;
          }
          setEditingRecord(null);
          setFormData({
            catalog_code: generateCatalogCode(),
            product_id: '',
            product_code: '',
            product_name: '',
            description: '',
            category_id: '',
            category: '',
            supplier_id: '',
            supplier_name: '',
            supplier_code: '',
            unit_price: '',
            currency: achatsConfig.defaultCurrency || getBrandDefaultCurrency(),
            unit: 'قطعة',
            min_order_qty: '',
            stock_quantity: '',
            reorder_level: '',
            delivery_time: achatsConfig.defaultLeadTimeDays ? String(achatsConfig.defaultLeadTimeDays) : '',
            notes: '',
            status: 'disponible',
          });
          setFormTab('mainInfo');
          setShowModal(true);
        }}
        createLabel={p('create')}
        importExport={importExportProps}
        headerActions={
          <button type="button" onClick={loadCatalogue} className="refresh-button" title={t('common.refresh', { defaultValue: 'تحديث' })}>
            <RefreshCcw size={20} />
          </button>
        }
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={p('advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {wrapControlsStripSearch(viewMode, (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder', { defaultValue: t('common.search', { defaultValue: 'بحث...' }) })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards', { defaultValue: 'بطاقات' })}
                  tableTitle={t('common.view_table', { defaultValue: 'جدول' })}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColumnVisible('catalog_code') ? <TableColumnHeaderFilter {...colHeader('catalog_code', p('col_catalog_code'))} /> : null}
                  {isColumnVisible('product_name') ? <TableColumnHeaderFilter {...colHeader('product_name', isHierarchyView ? p('col_supplier_products') : p('col_product'))} /> : null}
                  {!isHierarchyView && isColumnVisible('supplier_name') ? (
                    <TableColumnHeaderFilter {...colHeader('supplier_name', p('label_supplier'))} />
                  ) : null}
                  {isColumnVisible('unit_price') ? <TableColumnHeaderFilter {...colHeader('unit_price', p('col_price'))} /> : null}
                  {isColumnVisible('stock_quantity') ? <TableColumnHeaderFilter {...colHeader('stock_quantity', p('col_stock'))} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', p('col_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {isHierarchyView ? (
                  // Hierarchical View (Grouped by Supplier)
                  supplierGroups.length > 0 ? (
                    <>
                      {supplierGroups.map((supplierGroup) => {
                      const isExpanded = expandedSuppliers[supplierGroup.id] !== false; // Default to expanded
                      const supplierProducts = supplierGroup.products;
                      
                      return (
                        <React.Fragment key={supplierGroup.id}>
                          {/* Supplier Header Row (Expandable) */}
                          <tr 
                            onClick={() => toggleSupplierGroup(supplierGroup.id)}
                            className="bg-blue-500/10 hover:bg-blue-500/20 cursor-pointer border-t-2 border-blue-500/30"
                          >
                            <td className="px-3 py-4 text-center">
                              <button className="text-blue-400 hover:text-blue-300 transition-colors">
                                {isExpanded ? <ChevronDown size={20} /> : <ChevronUp size={20} />}
                              </button>
                            </td>
                            <td colSpan={hierarchyGroupColSpan} className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <Building size={20} className="text-blue-400" />
                                <div className="flex flex-col">
                                  <span className="text-white font-bold text-lg">{supplierGroup.name}</span>
                                  <span className="text-white/60 text-sm">{p('products_count', { count: supplierProducts.length })}</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                          
                          {/* Product Rows (Nested under supplier) */}
                          {isExpanded && supplierProducts.map((record, index) => (
                            <tr 
                              key={record.id} 
                              className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                            >
                              <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                                <div className="flex justify-center">
                                  <QawaemTableCheckbox
                                    checked={selectedRecords.includes(record.id)}
                                    onChange={() => handleSelectRecord(record.id)}
                                  />
                                </div>
                              </td>
                              {isColumnVisible('catalog_code') ? (
                              <td className="px-6 py-4 pl-12">
                                <span className="text-white font-mono font-semibold">{record.catalog_code || '—'}</span>
                              </td>
                              ) : null}
                              {isColumnVisible('product_name') ? (
                              <td className="px-6 py-4 pl-12">
                                <div className="flex items-center gap-2">
                                  <div className="w-1 h-8 bg-blue-400/30 rounded-full"></div>
                                  <div className="flex flex-col">
                                    <span className="text-white font-semibold">{record.product_name}</span>
                                    <span className="text-white/50 text-xs mt-0.5">{record.product_code || 'N/A'} | {record.category || p('uncategorized')}</span>
                                  </div>
                                </div>
                              </td>
                              ) : null}
                              {isColumnVisible('unit_price') ? (
                              <td className="px-6 py-4">
                                <div className="flex flex-col">
                                  <span className="text-white font-semibold">{formatBrandMoney(record.unit_price || 0)}</span>
                                  <span className="text-white/50 text-xs mt-0.5">{p('label_per_unit', { unit: record.unit || p('unit_piece') })}</span>
                                </div>
                              </td>
                              ) : null}
                              {isColumnVisible('stock_quantity') ? (
                              <td className="px-6 py-4">
                                <div className="flex flex-col">
                                  <span className="text-white font-semibold">{record.stock_quantity > 0 ? record.stock_quantity : '-'}</span>
                                  {achatsConfig.lowStockAlertEnabled && isAchatsCatalogLowStock(record) ? (
                                    <span className="text-amber-400 text-xs mt-0.5">{p('badge_low_stock', { defaultValue: 'مخزون منخفض' })}</span>
                                  ) : null}
                                  {record.reorder_level > 0 && (
                                    <span className="text-white/50 text-xs mt-0.5">{p('threshold_prefix')} {record.reorder_level}</span>
                                  )}
                                </div>
                              </td>
                              ) : null}
                              {isColumnVisible('status') ? (
                              <td className="px-6 py-4 text-center">
                                <div className="flex justify-center">
                                  <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                                </div>
                              </td>
                              ) : null}
                              <td className="px-6 py-4">
                                <QawaemErpRowActions
                                  onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                                  onEdit={() => handleEdit(record)}
                                  onDelete={() => handleDelete(record.id)}
                                />
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </>
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <Package size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                    </td>
                  </tr>
                )
) : (
  // Simple Flat View
  filteredCatalogue.length > 0 ? (
                    displayedCatalogue.map((record, index) => (
                      <tr 
                        key={record.id} 
                        className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                      >
                        <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                                <div className="flex justify-center">
                                  <QawaemTableCheckbox
                                    checked={selectedRecords.includes(record.id)}
                                    onChange={() => handleSelectRecord(record.id)}
                                  />
                                </div>
                              </td>
                        {isColumnVisible('catalog_code') ? (
                        <td className="px-6 py-4">
                          <span className="text-white font-mono font-semibold">{record.catalog_code || '—'}</span>
                        </td>
                        ) : null}
                        {isColumnVisible('product_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.product_name}</span>
                            <span className="text-white/50 text-xs mt-0.5">{record.product_code || 'N/A'} | {record.category_name || record.category || p('uncategorized')}</span>
                          </div>
                        </td>
                        ) : null}
                        {isColumnVisible('supplier_name') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">
                              {record.supplier_name || (() => {
                                const supplier = availableSuppliers.find(s => s.id.toString() === record.supplier_id?.toString());
                                return supplier ? (supplier.company_name || supplier.name) : 'N/A';
                              })()}
                            </span>
                            <span className="text-white/50 text-xs mt-0.5">{record.supplier_code || 'N/A'}</span>
                          </div>
                        </td>
                        ) : null}
                        {isColumnVisible('unit_price') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{formatBrandMoney(record.unit_price || 0)}</span>
                            <span className="text-white/50 text-xs mt-0.5">{p('label_per_unit', { unit: record.unit_of_measure || record.unit || p('unit_piece') })}</span>
                          </div>
                        </td>
                        ) : null}
                        {isColumnVisible('stock_quantity') ? (
                        <td className="px-6 py-4">
                          <div className="flex flex-col">
                            <span className="text-white font-semibold">{record.stock_quantity > 0 ? record.stock_quantity : '-'}</span>
                            {achatsConfig.lowStockAlertEnabled && isAchatsCatalogLowStock(record) ? (
                              <span className="text-amber-400 text-xs mt-0.5">{p('badge_low_stock', { defaultValue: 'مخزون منخفض' })}</span>
                            ) : null}
                            {record.reorder_level > 0 && (
                              <span className="text-white/50 text-xs mt-0.5">{p('threshold_prefix')} {record.reorder_level}</span>
                            )}
                          </div>
                        </td>
                        ) : null}
                        {isColumnVisible('status') ? (
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center">
                            <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                          </div>
                        </td>
                        ) : null}
                        <td className="px-6 py-4">
                          <QawaemErpRowActions
                            onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                            onEdit={() => handleEdit(record)}
                            onDelete={() => handleDelete(record.id)}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                        <Package size={48} className="mx-auto mb-4 text-white/30" />
                        <p className="text-lg">{p('empty_title')}</p>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedCatalogue.length > 0 ? (
              displayedCatalogue.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <Package size={20} className="text-emerald-400" />
                          {record.product_name}
                        </h3>
                        <div className="flex">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_product_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_code')}:</span> {record.product_code || 'N/A'}</div>
                            <div><span className="font-medium">{p('label_category')}:</span> {record.category || 'N/A'}</div>
                            <div><span className="font-medium">{p('label_unit')}:</span> {record.unit || 'N/A'}</div>
                            {record.description && (
                              <div className="pt-2 border-t border-white/20">
                                <span className="font-medium text-white">{p('label_description')}:</span>
                                <div className="text-white/90 text-xs mt-1">{record.description}</div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('label_supplier')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}:</span> {record.supplier_name || 'N/A'}</div>
                            <div><span className="font-medium">{p('label_code')}:</span> {record.supplier_code || 'N/A'}</div>
                            <div><span className="font-medium">{p('label_delivery')}:</span> {p('days_suffix', { count: record.delivery_time || 0 })}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_price_stock')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_price')}:</span> {formatBrandMoney(record.unit_price || 0)}</div>
                            <div><span className="font-medium">{p('label_stock')}:</span> {record.stock_quantity || 0}</div>
                            <div><span className="font-medium">{p('label_threshold')}:</span> {record.reorder_level || 0}</div>
                            <div><span className="font-medium">{p('label_min_qty')}:</span> {record.min_order_qty || 1}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.notes && (
                              <div className="p-2 bg-white/5 rounded">
                                <span className="font-medium text-white/80 text-xs">{p('label_notes')}:</span>
                                <p className="text-white/60 text-xs mt-1">{record.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <Package size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      {/* View Modal */}
      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => {
          setShowViewModal(false);
          setViewingRecord(null);
        }}
        title={p('view_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>
                    <Package size={18} className="text-emerald-400" />
                    {p('card_product_info')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('label_name')}</p>
                      <p className={m.viewValueLg}>{viewingRecord.product_name}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_code')}</p>
                      <p className={m.viewValue}>{viewingRecord.product_code || 'N/A'}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_category')}</p>
                      <p className={m.viewValue}>{viewingRecord.category || 'N/A'}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('label_unit')}</p>
                      <p className={m.viewValue}>{viewingRecord.unit || 'N/A'}</p>
                    </div>
                    <div className="md:col-span-4">
                      <p className={m.viewLabel}>{p('label_description')}</p>
                      <p className={m.viewValue}>{viewingRecord.description || p('no_description')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}>
                      <Truck size={18} className="text-blue-400" />
                      {p('label_supplier')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className={m.viewLabel}>{p('label_name')}</p>
                        <p className={m.viewValue}>{viewingRecord.supplier_name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('label_code')}</p>
                        <p className={m.viewValue}>{viewingRecord.supplier_code || 'N/A'}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('view_delivery_time')}</p>
                        <p className={m.viewValue}>{p('days_suffix', { count: viewingRecord.delivery_time || 0 })}</p>
                      </div>
                    </div>
                  </div>

                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}>
                      <DollarSign size={18} className="text-green-400" />
                      {p('card_price_stock')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className={m.viewLabel}>{p('view_unit_price')}</p>
                        <p className={m.viewValueLg}>{formatBrandMoney(viewingRecord.unit_price || 0)}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('view_stock_available')}</p>
                        <p className={m.viewValue}>{viewingRecord.stock_quantity || 0}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('view_reorder_level')}</p>
                        <p className={m.viewValue}>{viewingRecord.reorder_level || 0}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('view_min_order_qty')}</p>
                        <p className={m.viewValue}>{viewingRecord.min_order_qty || 1}</p>
                      </div>
                    </div>
                  </div>
                </div>
        </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Edit/Create Modal */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingRecord ? p('modal_edit') : p('modal_create')}
        icon={Package}
        formId="catalogue-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="catalogue-form" onSubmit={handleSubmit}>
              {formTab === 'mainInfo' && (
                <div className={ACHATS_FORM_GRID}>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('col_catalog_code')}</label>
                    <input
                      type="text"
                      value={formData.catalog_code}
                      onChange={(e) => setFormData({ ...formData, catalog_code: e.target.value })}
                      className={`${cfgTheme.input} font-mono`}
                      placeholder={p('placeholder_auto_generated', { defaultValue: 'يُولَّد تلقائياً' })}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>
                      <RequiredLabelText text={p('field_product')} required />
                    </label>
                    {loadingProducts ? (
                      <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                    ) : (
                      <CustomSelect
                        value={formData.product_id}
                        onChange={(value) => handleProductSelection(value)}
                        options={availableProducts.map((product) => ({
                          value: product.id,
                          label: `${product.name || ''}${product.code ? ` (${product.code})` : ''}`,
                        }))}
                        placeholder={p('placeholder_select_product')}
                        searchable
                      />
                    )}
                  </div>
                  <div>
                    <label className={cfgTheme.label}>
                      <RequiredLabelText text={p('label_supplier')} required />
                    </label>
                    {loadingSuppliers ? (
                      <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                    ) : (
                      <CustomSelect
                        value={formData.supplier_id}
                        onChange={(value) => handleSupplierSelection(value)}
                        options={availableSuppliers.map((supplier) => ({
                          value: supplier.id,
                          label: `${supplier.company_name || supplier.name || p('supplier_unnamed')} - ${supplier.contact_person || supplier.phone1 || ''}`,
                        }))}
                        placeholder={p('placeholder_select_supplier')}
                        searchable
                      />
                    )}
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_category')}</label>
                    {loadingCategories ? (
                      <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading')}</div>
                    ) : (
                      <CustomSelect
                        value={formData.category_id}
                        onChange={(value) => handleCategorySelection(value)}
                        options={availableCategories.map((category) => ({
                          value: category.id,
                          label: category.name,
                        }))}
                        placeholder={p('select_placeholder')}
                        searchable
                      />
                    )}
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_description')}</label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_product_description')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}><RequiredLabelText text={p('col_status')} required /></label>
                    <CustomSelect
                      value={formData.status}
                      onChange={(value) => setFormData({ ...formData, status: value })}
                      options={statusOptions.map((option) => ({ value: option.value, label: option.label }))}
                      placeholder={p('select_placeholder')}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('label_unit')}</label>
                    <CustomSelect
                      value={formData.unit}
                      onChange={(value) => setFormData({ ...formData, unit: value })}
                      options={UNIT_OPTIONS.map((option) => ({ value: option.value, label: p(option.labelKey) }))}
                      placeholder={p('select_placeholder')}
                    />
                  </div>
                </div>
              )}

              {formTab === 'pricingStock' && (
                <div className={ACHATS_FORM_GRID}>
                  <div>
                    <label className={cfgTheme.label}>{p('field_unit_price')}</label>
                    <CustomNumberInput
                      value={formData.unit_price}
                      onChange={(value) => setFormData({ ...formData, unit_price: value })}
                      placeholder="0.00"
                      step={0.01}
                      min={0}
                      decimals={2}
                      required
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('field_currency', { defaultValue: 'العملة' })}</label>
                    <CustomSelect
                      value={formData.currency}
                      onChange={(value) => setFormData({ ...formData, currency: normalizeBrandCurrency(value) || getBrandDefaultCurrency() })}
                      options={getBrandCurrencyOptions()}
                      placeholder={p('select_placeholder')}
                      searchable
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('field_min_order')}</label>
                    <CustomNumberInput
                      value={formData.min_order_qty}
                      onChange={(value) => setFormData({ ...formData, min_order_qty: value })}
                      placeholder="1"
                      min={1}
                      decimals={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('col_stock')}</label>
                    <CustomNumberInput
                      value={formData.stock_quantity}
                      onChange={(value) => setFormData({ ...formData, stock_quantity: value })}
                      placeholder="0"
                      min={0}
                      decimals={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('field_reorder')}</label>
                    <CustomNumberInput
                      value={formData.reorder_level}
                      onChange={(value) => setFormData({ ...formData, reorder_level: value })}
                      placeholder="0"
                      min={0}
                      decimals={0}
                    />
                  </div>
                  <div>
                    <label className={cfgTheme.label}>{p('field_delivery_days')}</label>
                    <CustomNumberInput
                      value={formData.delivery_time}
                      onChange={(value) => setFormData({ ...formData, delivery_time: value })}
                      placeholder={p('placeholder_delivery_example')}
                      min={0}
                      decimals={0}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_notes')}</label>
                    <textarea
                      rows={3}
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_notes')}
                    />
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default CatalogueFournisseurPage;


